import Stack from '@ui/layout/Stack';
import EpisodeEditFormChaptersAudioPlayer from '@app/organisms/EpisodeEditForm/EpisodeEditFormChapters/EpisodeEditFormChaptersAudioPlayer';
import EpisodeEditFormChaptersLabel from '@app/organisms/EpisodeEditForm/EpisodeEditFormChapters/EpisodeEditFormChaptersLabel';
import NoChaptersAlert from '@app/organisms/EpisodeEditForm/EpisodeEditFormChapters/NoChaptersAlert';
import EpisodeEditFormChaptersList from '@app/organisms/EpisodeEditForm/EpisodeEditFormChapters/EpisodeEditFormChaptersList';
import SelectedChapter from '@app/organisms/EpisodeEditForm/EpisodeEditFormChapters/SelectedChapter';
import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { useSwitch } from '@hooks/useSwitch';
import { useOverlayTriggerState } from 'react-stately';
import EpisodeChapterDeleteModal from '@app/molecules/EpisodeChapterDeleteModal';
import NextStepButton from '../NextStepButton';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 2rem;

    ${(p) => p.theme.mediaQueries.desktopAndUp} {
        flex-direction: row;

        & > * {
            width: 50%;
        }
    }
`;

const EpisodeEditFormChaptersEditor = ({ episode, waveformUrl }) => {
    const { control } = useFormContext();
    const { fields, append, remove, update } = useFieldArray({
        name: 'chapters',
        keyName: 'key',
        control,
    });

    const [isAudioPlaying, playAudio, pauseAudio] = useSwitch(false);
    const [audioPlayerCursorPosition, setAudioPlayerCursorPosition] = useState(false);
    const [audioPlayerCursorPositionHasChanged, setAudioPlayerCursorPositionHasChanged] =
        useState(false);

    const [selectedChapterId, setSelectedChapterId] = useState();
    const selectedChapterIndex = fields.findIndex((chapter) => chapter.id === selectedChapterId);
    const selectedField = fields[selectedChapterIndex];

    const [chapterIdToDelete, setChapterIdToDelete] = useState();
    const chapterIndexToDelete = fields.findIndex((chapter) => chapter.id === chapterIdToDelete);
    const fieldToDelete = fields[chapterIndexToDelete];
    const deleteChapterModalState = useOverlayTriggerState({});

    const onSelectChapter = (chapterId) => {
        setSelectedChapterId(chapterId);
    };

    const onAddChapter = (newChapter) => {
        append(newChapter);
    };

    const onUpdateChapterStartTime = (chapterId, startTime) => {
        const chapterIndex = fields.findIndex((chapter) => chapter.id === chapterId);

        if (chapterIndex < 0) {
            return;
        }

        const chapterField = fields[chapterIndex];
        update(chapterIndex, { ...chapterField, startTime });
    };

    const handleDeleteChapterById = (chapterId) => {
        setChapterIdToDelete(chapterId);
        deleteChapterModalState.open();
    };

    const handleDeleteSelectedChapter = () => {
        handleDeleteChapterById(selectedChapterId);
    };

    const confirmDeleteChapter = () => {
        if (chapterIdToDelete === selectedChapterId) {
            setSelectedChapterId(undefined);
        }

        setChapterIdToDelete(undefined);
        remove(chapterIndexToDelete);
    };

    return (
        <Stack $gap="1rem">
            <EpisodeEditFormChaptersAudioPlayer
                audioUrl={episode.audioUrlInternal}
                waveformUrl={waveformUrl}
                selectedChapterId={selectedChapterId}
                onSelectChapter={onSelectChapter}
                onAddChapter={onAddChapter}
                onUpdateChapterStartTime={onUpdateChapterStartTime}
                cursorPosition={audioPlayerCursorPosition}
                cursorPositionHasChanged={audioPlayerCursorPositionHasChanged}
                isPlaying={isAudioPlaying}
                play={playAudio}
                pause={pauseAudio}
            />
            {fields.length === 0 ? (
                <Stack $gap="1rem">
                    <EpisodeEditFormChaptersLabel />
                    <NoChaptersAlert />
                </Stack>
            ) : (
                <Container>
                    <Stack $gap="1rem">
                        <EpisodeEditFormChaptersLabel />
                        <EpisodeEditFormChaptersList
                            selectedChapterId={selectedChapterId}
                            onSelectChapter={setSelectedChapterId}
                            onDeleteChapter={handleDeleteChapterById}
                        />
                    </Stack>

                    {!!selectedField && (
                        <SelectedChapter
                            key={selectedField.key}
                            index={selectedChapterIndex}
                            onDeleteChapter={handleDeleteSelectedChapter}
                            setAudioPlayerCursorPosition={setAudioPlayerCursorPosition}
                            setAudioPlayerCursorPositionHasChanged={
                                setAudioPlayerCursorPositionHasChanged
                            }
                            isAudioPlaying={isAudioPlaying}
                            playAudio={playAudio}
                        />
                    )}
                </Container>
            )}
            <NextStepButton />
            {deleteChapterModalState.isOpen && (
                <EpisodeChapterDeleteModal
                    chapter={fieldToDelete}
                    handleDeleteChapter={confirmDeleteChapter}
                    isOpen={deleteChapterModalState.isOpen}
                    onClose={deleteChapterModalState.close}
                />
            )}
        </Stack>
    );
};

export default EpisodeEditFormChaptersEditor;
